import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from '@startupquickstart/frontend';

interface ImageCellProps<T> {
  /** The item to display/edit the image for */
  item?: T;
  /** The endpoint to use for updating (e.g., '/tours', '/hotels') */
  endpoint: string;
  /** The entity name for query invalidation (e.g., 'Tour', 'Hotel') */
  entityName: string;
  /** The label for the upload modal (e.g., 'Tour Image', 'Hotel Image') */
  imageLabel: string;
  /** Function to extract the featured image from the item */
  getFeaturedImage: (item: T) => { id: string; name?: string; url?: string } | undefined;
  /** Function to extract the item ID */
  getItemId: (item: T) => string;
  /** Size variant: 'sm' for list pages (64x64), 'lg' for detail pages (256x192) */
  size?: 'sm' | 'lg';
  /**
   * Optional extra action(s) rendered inside the hover overlay, alongside the
   * built-in upload/delete controls (e.g. an "AI generate" button). Rendered in
   * both the empty and filled states. Handlers must `stopPropagation` so they
   * don't trigger the cell's upload/row click.
   */
  overlayActions?: ReactNode;
  /**
   * Optional overlay rendered on top of the cell regardless of hover — used for
   * async status such as an in-progress spinner or an error badge. It should be
   * absolutely positioned (`absolute inset-0`) and sits above the hover overlay.
   */
  statusOverlay?: ReactNode;
}

/**
 * Generic editable image cell component with upload and delete actions
 * Can be used for any entity type (tours, hotels, greater areas, etc.)
 */
export function ImageCell<T>({
  item,
  endpoint,
  entityName,
  imageLabel,
  getFeaturedImage,
  getItemId,
  size = 'sm',
  overlayActions,
  statusOverlay,
}: ImageCellProps<T>) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [newMediaId, setNewMediaId] = useState<string | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  if (!item) return null;

  // Size classes
  const sizeClasses = {
    container: size === 'lg' ? 'w-64 h-48' : 'w-16 h-16',
    icon: size === 'lg' ? 'h-8 w-8' : 'h-6 w-6',
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setUploadModalOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setDeleteModalOpen(true);
  };

  const handleUploadSubmit = async () => {
    if (!newMediaId) return;

    setIsUploading(true);
    try {
      const itemId = getItemId(item);
      await apiClient.patch(`${endpoint}/${itemId}`, {
        featuredImageId: newMediaId,
      });
      toast.success('Featured image added');
      setUploadModalOpen(false);
      setNewMediaId(undefined);
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: [entityName] });
    } catch (error) {
      toast.error('Failed to add image');
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const itemId = getItemId(item);
      await apiClient.patch(`${endpoint}/${itemId}`, {
        featuredImageId: null,
      });
      toast.success('Featured image removed');
      setDeleteModalOpen(false);
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: [entityName] });
    } catch (error) {
      toast.error('Failed to remove image');
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const featuredImage = getFeaturedImage(item);
  const imageUrl = featuredImage?.url ?? null;

  if (!imageUrl) {
    return (
      <>
        <div
          className={`relative group ${sizeClasses.container} bg-primary/10 rounded flex items-center justify-center cursor-pointer hover:bg-primary/20 transition-colors`}
          onClick={handleUploadClick}
        >
          <ImageIcon className={`${sizeClasses.icon} text-primary`} />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
            <Plus className={`${sizeClasses.icon} text-white`} />
            {overlayActions}
          </div>
          {statusOverlay}
        </div>

        {/* Upload Modal */}
        <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>Add Featured Image</DialogTitle>
              <DialogDescription>Upload an image for this {entityName.toLowerCase()}</DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <ImageUploadDropzone targetAspectRatio={4/3}
                value={newMediaId}
                onChange={setNewMediaId}
                label={imageLabel}
                required
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUploadSubmit} disabled={!newMediaId || isUploading}>
                {isUploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Image
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <div className={`relative group ${sizeClasses.container}`}>
        <img
          src={imageUrl}
          alt={featuredImage?.name || `${entityName} image`}
          className="w-full h-full object-cover rounded"
        />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
          {overlayActions}
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="h-7 w-7 p-0"
            title="Remove image"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
        {statusOverlay}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Remove Featured Image</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this image from the {entityName.toLowerCase()}? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
